import React, {
  useCallback,
  useEffect,
  useState
} from "react";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";

import authService from "../../services/authService";
import settingsService from "../../services/settingsService";
import dealerService from "../../services/dealerService";

const SuperAdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [adminHierarchy, setAdminHierarchy] = useState([]);
  const [adminSummary, setAdminSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        }

        const [dashboardRes, hierarchyRes] = await Promise.all([
          settingsService.getDashboard(),
          dealerService.getHierarchy()
        ]);

        if (dashboardRes?.success) {
          setDashData(dashboardRes.dashboard || {});
        }

        if (hierarchyRes?.success) {
          setAdminHierarchy(
            Array.isArray(hierarchyRes.hierarchy)
              ? hierarchyRes.hierarchy
              : []
          );

          setAdminSummary(
            hierarchyRes.summary || null
          );
        }
      } catch (error) {
        if (!silent) {
          toast.error(
            error.response?.data?.message ||
            "Failed to load dashboard data."
          );
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    setUser(
      authService.getCurrentUser()
    );

    fetchDashboardData();
  }, [fetchDashboardData]);

  const toggleSidebar = () =>
    setSidebarOpen(
      (previous) => !previous
    );

  const closeSidebar = () =>
    setSidebarOpen(false);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="flex-grow-1 p-3 p-lg-4 overflow-auto">
          <div className="container-fluid">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4">
              <div>
                <h2 className="fw-bold text-dark mb-1">
                  Super Admin Portal
                </h2>

                <p className="text-secondary mb-0">
                  Welcome back,{" "}
                  <span className="fw-semibold text-primary">
                    {user?.full_name ||
                      user?.username}
                  </span>
                  !
                </p>
              </div>

              <button
                type="button"
                className="btn btn-light border btn-sm mt-3 mt-sm-0 align-self-start align-self-sm-center px-3"
                onClick={() =>
                  fetchDashboardData()
                }
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Refresh Data
              </button>
            </div>

            {loading ? (
              <Loader message="Loading Super Admin dashboard..." />
            ) : (
              <>
                <div className="admin-hero mb-4">
                  <div>
                    <span className="admin-eyebrow">
                      CONTROL CENTER
                    </span>

                    <h3 className="mb-2">
                      System overview at a glance
                    </h3>

                    <p className="mb-0">
  Monitor your dealer network, shops, computers and
  activity from one place.
</p>
                  </div>

                  <div className="admin-hero-actions">
                    <Link
                      to="/dealers"
                      className="btn btn-light"
                    >
                      <i className="bi bi-people me-2" />
                      Manage Dealers
                    </Link>

                    <Link
                      to="/transfer-dealer"
                      className="btn btn-warning"
                    >
                      <i className="bi bi-arrow-left-right me-2" />
                      Transfer Coins
                    </Link>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                 {[
  [
    "Total Dealers",
    adminSummary?.total_dealers ??
      dashData?.total_dealers ??
      0,
    "bi-people-fill",
    "primary"
  ],
  [
    "Total Shops",
    adminSummary?.total_shops ??
      dashData?.total_shops ??
      0,
    "bi-shop",
    "warning"
  ],
  [
    "Total Computers",
    adminSummary?.total_computers ?? 0,
    "bi-pc-display",
    "info"
  ],
  [
    "Activity Records",
    adminSummary?.game_summary?.total_activity ?? 0,
    "bi-bar-chart-fill",
    "success"
  ]
].map(
                    ([
                      label,
                      value,
                      icon,
                      tone
                    ]) => (
                      <div
                        className="col-12 col-sm-6 col-xl-3"
                        key={label}
                      >
                        <div className="admin-stat-card h-100">
                          <div
                            className={`admin-stat-icon tone-${tone}`}
                          >
                            <i
                              className={`bi ${icon}`}
                            />
                          </div>

                          <div>
                            <div className="admin-stat-label">
                              {label}
                            </div>

                            <div className="admin-stat-value">
                              {Number(
                                value
                              ).toLocaleString(
                                "en-IN"
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-8">
                    <div className="card h-100">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="fw-bold mb-1">
                              Dealer Network
                            </h5>

                            <p className="text-secondary small mb-0">
                              Live hierarchy summary from Admin API.
                            </p>
                          </div>

                          <Link
                            to="/hierarchy"
                            className="btn btn-sm btn-light border"
                          >
                            View all
                          </Link>
                        </div>

                        <div className="admin-network-list">
                          {adminHierarchy
                            .slice(0, 4)
                            .map(
                              (dealer) => (
                                <div
                                  className="admin-network-row"
                                  key={dealer.id}
                                >
                                  <div className="admin-network-avatar">
                                    {String(
                                      dealer.full_name ||
                                      dealer.username ||
                                      "D"
                                    )
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>

                                  <div className="flex-grow-1 min-width-0">
                                    <div className="fw-bold text-dark text-truncate">
                                      {dealer.full_name ||
                                        dealer.username}
                                    </div>

                                    <div className="small text-secondary">
  @{dealer.username} ·{" "}
  {dealer.total_shops || 0}{" "}
  shops ·{" "}
  {dealer.total_computers || 0}{" "}
  computers
</div>
                                  </div>

                                  <div className="text-end">
                                    <span
                                      className={`badge ${dealer.status ===
                                          "ACTIVE"
                                          ? "text-bg-success"
                                          : "text-bg-secondary"
                                        }`}
                                    >
                                      {dealer.status}
                                    </span>

                                    <div className="small fw-semibold mt-1">
                                      🪙{" "}
                                      {Number(
                                        dealer.wallet ||
                                        0
                                      ).toLocaleString(
                                        "en-IN"
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            )}

                          {!adminHierarchy.length && (
                            <div className="text-secondary small py-4 text-center">
                              No dealer hierarchy available.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-xl-4">
                    <div className="card h-100">
                      <div className="card-body p-4">
                        <h5 className="fw-bold mb-1">
  Computer Activity
</h5>

<p className="text-secondary small mb-3">
  Read-only computer activity summary.
</p>

                      {[
  [
    "Activity Records",
    adminSummary?.game_summary
      ?.total_activity ?? 0,
    "bi-activity"
  ],
  [
    "Credits Used",
    adminSummary?.game_summary
      ?.total_credits_used ?? 0,
    "bi-coin"
  ],
  [
    "Wins / Losses",
    `${
      adminSummary?.game_summary
        ?.total_wins ?? 0
    } / ${
      adminSummary?.game_summary
        ?.total_losses ?? 0
    }`,
    "bi-bar-chart"
  ],
  [
    "Recorded Winning Credits",
    adminSummary?.game_summary
      ?.total_winning_credits ?? 0,
    "bi-trophy"
  ]
].map(
                          ([
                            label,
                            value,
                            icon
                          ]) => (
                            <div
                              className="admin-snapshot-row"
                              key={label}
                            >
                              <span>
                                <i
                                  className={`bi ${icon} me-2`}
                                />
                                {label}
                              </span>

                              <strong>
                                {typeof value ===
                                  "number"
                                  ? Number(
                                    value || 0
                                  ).toLocaleString(
                                    "en-IN"
                                  )
                                  : value ?? 0}
                              </strong>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
